import { useMemo, useState } from "react";

import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Divider,
  Paper,
  TextField,
  Typography,
} from "@mui/material";

import { motion, Variants } from "framer-motion";

import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import BugReportOutlinedIcon from "@mui/icons-material/BugReportOutlined";
import FeedbackOutlinedIcon from "@mui/icons-material/FeedbackOutlined";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import { useSendContactMessage } from "../hooks/useContact";
import PageMeta from '../components/PageMeta';

const fadeUp: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.08,
      duration: 0.4,
      ease: "easeOut" as const,
    },
  }),
};

export default function ContactPage() {
  const pageLoadTime = useMemo(() => Date.now(), []);

  const { mutateAsync: sendContact, isPending } = useSendContactMessage();

  const [form, setForm] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
    website: "", // Honeypot field
  });

  const [loading, setLoading] = useState(false);

  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setSuccessMessage("");
    setForm((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const validate = () => {
    if (!form.name.trim()) {
      return "Please enter your name.";
    }

    if (!form.email.trim()) {
      return "Please enter your email.";
    }

    if (!form.subject.trim()) {
      return "Please enter a subject.";
    }

    if (!form.message.trim()) {
      return "Please enter a message.";
    }

    if (form.message.trim().length < 10) {
      return "Message is too short.";
    }

    return null;
  };

  const handleSubmit = async (
    e: React.FormEvent<HTMLFormElement>
  ) => {
    if (loading) return;
    e.preventDefault();

    setSuccessMessage("");
    setErrorMessage("");

    const validationError = validate();

    if (validationError) {
      setErrorMessage(validationError);
      return;
    }

    // Honeypot check
    if (form.website) {
      return;
    }

    // Minimum submission time
    const secondsOnPage =
      (Date.now() - pageLoadTime) / 1000;

    if (secondsOnPage < 3) {
      setErrorMessage(
        "Please wait a moment before submitting."
      );
      return;
    }

    try 
    {
        setLoading(true);

        await sendContact({
            name: form.name.trim(),
            email: form.email.trim(),
            subject: form.subject.trim(),
            message: form.message.trim(),
            website: form.website,
            submittedAt: pageLoadTime,
        });

        setSuccessMessage("Message sent successfully. Thanks for reaching out!");

        setForm({
            name: "",
            email: "",
            subject: "",
            message: "",
            website: "",
        });
      } 
      catch (err: any) 
      {
        console.error(err);

        const status = err?.response?.status;

        if (status === 429) {
            setErrorMessage("Too many requests. Please wait a few minutes.");
            return;
        }

        setErrorMessage(
            err?.response?.data?.message ||
            "Something went wrong while sending your message."
        );
      } 
      finally {
          setLoading(false);
      }
    }

  return (
    <>
      <PageMeta
        title="Contact | The Quizzler"
        description="Get in touch with The Quizzler team — report a question error, suggest new content, or just say hello."
        canonical="https://thequizzler.app/contact"
      />
      <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Typography
            variant="h3"
            fontWeight={700}
            gutterBottom
            sx={{ letterSpacing: "-0.5px" }}
          >
            Contact Us
          </Typography>

          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ mb: 4, maxWidth: 600 }}
          >
            Questions, bug reports, feedback, or quiz corrections?
            We'd love to hear from you.
          </Typography>

          <Divider sx={{ mb: 4 }} />
        </motion.div>

        {/* Info cards */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "repeat(3, 1fr)",
            },
            gap: 2,
            mb: 4,
          }}
        >
          {[
            {
              icon: <BugReportOutlinedIcon fontSize="small" />,
              title: "Bug reports",
              text: "Found an issue or broken question? Let us know.",
            },
            {
              icon: <FeedbackOutlinedIcon fontSize="small" />,
              title: "Feedback",
              text: "Suggestions and feature ideas are always welcome.",
            },
            {
              icon: <EmailOutlinedIcon fontSize="small" />,
              title: "Support",
              text: "Need help with your account or leaderboard stats?",
            },
          ].map((item, i) => (
            <motion.div
              key={item.title}
              custom={i}
              initial="hidden"
              animate="visible"
              variants={fadeUp}
            >
              <Paper
                variant="outlined"
                sx={{
                  p: 2.5,
                  borderRadius: 2,
                  height: "100%",
                }}
              >
                <Box
                  sx={{
                    width: 34,
                    height: 34,
                    borderRadius: 1,
                    bgcolor: "#7e57c2",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: 1.5,
                    "& svg": {
                      color: "white",
                    },
                  }}
                >
                  {item.icon}
                </Box>

                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  gutterBottom
                >
                  {item.title}
                </Typography>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ lineHeight: 1.7 }}
                >
                  {item.text}
                </Typography>
              </Paper>
            </motion.div>
          ))}
        </Box>

        {/* Contact form */}
        <motion.div
          custom={3}
          initial="hidden"
          animate="visible"
          variants={fadeUp}
        >
          <Paper
            variant="outlined"
            sx={{
              p: { xs: 2.5, md: 4 },
              borderRadius: 2,
            }}
          >
            <Box
              component="form"
              onSubmit={handleSubmit}
              noValidate
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "1fr 1fr",
                  },
                  gap: 2,
                  mb: 2,
                }}
              >
                <TextField
                  label="Name"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  fullWidth
                  required
                />

                <TextField
                  label="Email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  fullWidth
                  required
                />
              </Box>

              <TextField
                label="Subject"
                name="subject"
                value={form.subject}
                onChange={handleChange}
                fullWidth
                required
                sx={{ mb: 2 }}
              />

              <TextField
                label="Message"
                name="message"
                value={form.message}
                onChange={handleChange}
                fullWidth
                required
                multiline
                minRows={6}
                sx={{ mb: 2 }}
              />

              {/* Honeypot field */}
              <Box
                sx={{
                  position: "absolute",
                  left: "-9999px",
                  opacity: 0,
                  pointerEvents: "none",
                }}
                aria-hidden="true"
              >
                <TextField
                  tabIndex={-1}
                  autoComplete="off"
                  label="Website"
                  name="website"
                  value={form.website}
                  onChange={handleChange}
                />
              </Box>

              {errorMessage && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {errorMessage}
                </Alert>
              )}

              {successMessage && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  {successMessage}
                </Alert>
              )}

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  flexWrap: "wrap",
                }}
              >
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={loading}
                  endIcon={<SendRoundedIcon />}
                >
                  {loading ? "Sending..." : "Send Message"}
                </Button>

                <Chip
                  label="Protected against spam & abuse"
                  variant="outlined"
                  size="small"
                />
              </Box>
            </Box>
          </Paper>
        </motion.div>
      </Container>
    </>
  );
}